import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { OTPVerificationStep } from './RegisterPage';

// ── Shared nav + logo (same as RegisterPage) ──────────────────────────────────
const AuthNav = () => (
  <nav className="bg-white border-b border-border px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-10">
    <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-primary transition-colors">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
      </svg>
      Back to Home
    </Link>
    <Link to="/shop" className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
      Browse Products
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
      </svg>
    </Link>
  </nav>
);

const Logo = () => (
  <Link to="/" className="inline-block">
    <div className="leading-none text-center">
      <div className="text-3xl font-black tracking-tight text-primary">SKINN</div>
      <div className="text-[0.5rem] font-bold tracking-[0.35em] text-accent -mt-0.5 uppercase">Thrive</div>
    </div>
  </Link>
);

const Spinner = () => (
  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
  </svg>
);

// ── Login form ────────────────────────────────────────────────────────────────
const LoginForm = ({ onEmailNotVerified }) => {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || '/account';

  const [form, setForm]     = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back! 👋');
      navigate(from, { replace: true });
    } catch (err) {
      if (err.errorCode === 'EMAIL_NOT_VERIFIED') {
        onEmailNotVerified(err.email || form.email);
        return;
      }
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="text-center mb-8">
        <Logo />
        <h1 className="text-2xl font-bold text-ink mt-5 mb-1">Welcome back</h1>
        <p className="text-ink-soft text-sm">Sign in to your account</p>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-border" style={{boxShadow:'0 8px 40px rgba(124,31,166,0.10)'}}>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>

          {/* Inline error banner */}
          {error && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={e => { setForm(f => ({...f, email: e.target.value})); setError(''); }}
              placeholder="Enter your email address"
              className="input-field"
              required
              autoComplete="email"
            />
          </div>

          {/* Password */}
          <div>
            <div className="flex justify-between mb-1.5">
              <label className="text-sm font-semibold text-ink">Password</label>
              <Link to="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
            </div>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={form.password}
                onChange={e => { setForm(f => ({...f, password: e.target.value})); setError(''); }}
                placeholder="Enter your password"
                className="input-field pr-16"
                required
                minLength={6}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-ink-muted hover:text-primary transition-colors"
              >
                {showPwd ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3.5 text-base flex items-center justify-center gap-2"
          >
            {loading ? <><Spinner /> Signing in…</> : 'Sign In →'}
          </button>
        </form>

        <p className="text-center text-sm text-ink-soft mt-5">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary font-semibold hover:underline">Create one →</Link>
        </p>
      </div>
    </>
  );
};

// ── Main LoginPage ────────────────────────────────────────────────────────────
const LoginPage = () => {
  const { resendOtp }       = useAuth();
  const navigate            = useNavigate();
  const location            = useLocation();
  const from                = location.state?.from?.pathname || '/account';
  const [showOtp, setShowOtp]   = useState(false);
  const [otpEmail, setOtpEmail] = useState('');

  const handleEmailNotVerified = (email) => {
    setOtpEmail(email);
    setShowOtp(true);
  };

  const handleOtpSuccess = () => {
    toast.success('Email verified! Welcome to Skinn Thrive 🎉');
    navigate(from, { replace: true });
  };

  return (
    <>
      <Helmet><title>Sign In — Skinn Thrive</title></Helmet>
      <AuthNav />
      <div
        className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-12"
        style={{ background: 'linear-gradient(135deg, #F3E8FF 0%, #FCE4F3 100%)' }}
      >
        <div className="w-full max-w-md">
          {showOtp ? (
            <>
              {/* Email not verified banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-4 flex items-start gap-2.5">
                <svg className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                <p className="text-sm text-amber-800">
                  Your email is not verified. We've sent a new OTP to <strong>{otpEmail}</strong>.
                </p>
              </div>
              <OTPVerificationStep
                email={otpEmail}
                onSuccess={handleOtpSuccess}
                onBack={() => setShowOtp(false)}
                onResend={resendOtp}
                title="Verify your email"
                subtitle={<>Enter the 6-digit code sent to <strong className="text-ink">{otpEmail}</strong></>}
              />
            </>
          ) : (
            <LoginForm onEmailNotVerified={handleEmailNotVerified} />
          )}
        </div>
      </div>
    </>
  );
};

export default LoginPage;
